import type { Campaign, CampaignStatus, RuleGroup, TriggerConfig, TriggerType } from './types';
import { MOCK_BRAND_ID } from './mockReference';

export interface TriggerDef {
  type: TriggerType;
  /** Requirement ID */
  ruleId: string;
  name: string;
  icon: string;
  qualifiesWhen: string;
  reentry: string;
  defaultConfig: TriggerConfig;
  /** What the configurable number means; null when the trigger has no setting. */
  configLabel: string | null;
  configUnit: 'days' | 'percent' | null;
  requiresLoyalty: boolean;
  /** The Loyalty notification that covers the same moment, if any. */
  loyaltyOverlap: 'welcome' | 'reward-unlocked' | 'points-expiry' | 'birthday' | null;
  defaultSms: string;
  defaultEmailSubject: string;
  defaultEmailBody: string;
  defaultPushTitle: string;
  /** Approximation of who qualifies today, used to build mock report data. */
  mockAudienceRules: RuleGroup;
}

const and = (...children: RuleGroup['children']): RuleGroup => ({ op: 'and', children });

/** AUT-01 to AUT-09, listed in send priority order (AUT-15). */
export const TRIGGERS: TriggerDef[] = [
  {
    type: 'birthday', ruleId: 'AUT-04', name: 'Birthday', icon: 'cake',
    qualifiesWhen: 'Birthday is N days away', reentry: 'Once per calendar year',
    defaultConfig: { days: 7 }, configLabel: 'Days before the birthday', configUnit: 'days', requiresLoyalty: false, loyaltyOverlap: 'birthday',
    defaultSms: 'Happy early birthday, {{first_name}}! Come celebrate with us this week. {{link}}',
    defaultEmailSubject: 'Happy birthday from {{brand_name}}',
    defaultEmailBody: 'Hi {{first_name}}, your birthday is coming up and we would love to celebrate with you.',
    defaultPushTitle: 'Happy early birthday!',
    mockAudienceRules: and({ field: 'birthday_this_month', operator: 'eq', value: true }),
  },
  {
    type: 'welcome', ruleId: 'AUT-01', name: 'Welcome', icon: 'waving_hand',
    qualifiesWhen: 'First order completed', reentry: 'Once per customer ever',
    defaultConfig: { days: 1 }, configLabel: 'Days after the first order', configUnit: 'days', requiresLoyalty: false, loyaltyOverlap: null,
    defaultSms: 'Thanks for your first order, {{first_name}}! We hope to see you again soon. {{link}}',
    defaultEmailSubject: 'Welcome to {{brand_name}}',
    defaultEmailBody: 'Hi {{first_name}}, thank you for your first order. We are glad you found us.',
    defaultPushTitle: 'Welcome to {{brand_name}}',
    mockAudienceRules: and({ field: 'order_count', operator: 'eq', value: 1 }, { field: 'first_order_days_ago', operator: 'lt', value: 30 }),
  },
  {
    type: 'loyalty_welcome', ruleId: 'AUT-05', name: 'Loyalty welcome', icon: 'loyalty',
    qualifiesWhen: 'Joined loyalty', reentry: 'Once per customer ever',
    defaultConfig: { days: 0 }, configLabel: 'Days after joining', configUnit: 'days', requiresLoyalty: true, loyaltyOverlap: 'welcome',
    defaultSms: 'Welcome to our rewards program, {{first_name}}! You have {{points_balance}} points. {{link}}',
    defaultEmailSubject: 'You are in: welcome to rewards',
    defaultEmailBody: 'Hi {{first_name}}, you now earn points on every order. Your balance is {{points_balance}}.',
    defaultPushTitle: 'Welcome to rewards',
    mockAudienceRules: and({ field: 'loyalty_member', operator: 'eq', value: true }, { field: 'first_order_days_ago', operator: 'lt', value: 30 }),
  },
  {
    type: 'reward_unlocked', ruleId: 'AUT-07', name: 'Reward waiting', icon: 'redeem',
    qualifiesWhen: 'Reward available and unredeemed for N days', reentry: 'Once per reward',
    defaultConfig: { days: 3 }, configLabel: 'Days the reward has been waiting', configUnit: 'days', requiresLoyalty: true, loyaltyOverlap: 'reward-unlocked',
    defaultSms: '{{first_name}}, you have a reward waiting. Use it on your next order. {{link}}',
    defaultEmailSubject: 'Your reward is waiting',
    defaultEmailBody: 'Hi {{first_name}}, you have a reward ready to use on your next order.',
    defaultPushTitle: 'You have a reward waiting',
    mockAudienceRules: and({ field: 'has_unredeemed_reward', operator: 'eq', value: true }),
  },
  {
    type: 'points_expiring', ruleId: 'AUT-08', name: 'Points expiring', icon: 'hourglass_bottom',
    qualifiesWhen: 'Points expire within N days', reentry: 'Once per expiry batch',
    defaultConfig: { days: 14 }, configLabel: 'Days before the points expire', configUnit: 'days', requiresLoyalty: true, loyaltyOverlap: 'points-expiry',
    defaultSms: '{{first_name}}, some of your {{points_balance}} points expire soon. Use them before they go. {{link}}',
    defaultEmailSubject: 'Your points expire soon',
    defaultEmailBody: 'Hi {{first_name}}, some of your {{points_balance}} points are about to expire. Order soon to use them.',
    defaultPushTitle: 'Points expiring soon',
    mockAudienceRules: and({ field: 'points_expiring_within_days', operator: 'lte', value: 14 }),
  },
  {
    type: 'near_reward', ruleId: 'AUT-06', name: 'Close to a reward', icon: 'trending_up',
    qualifiesWhen: 'Points within N of the next reward', reentry: 'Once per reward cycle',
    defaultConfig: { percent: 20 }, configLabel: 'Points still needed, as a share of the reward cost', configUnit: 'percent', requiresLoyalty: true, loyaltyOverlap: null,
    defaultSms: 'So close, {{first_name}}! You have {{points_balance}} points, nearly enough for your next reward. {{link}}',
    defaultEmailSubject: 'You are almost at your next reward',
    defaultEmailBody: 'Hi {{first_name}}, with {{points_balance}} points you are one order away from your next reward.',
    defaultPushTitle: 'Almost at your next reward',
    mockAudienceRules: and({ field: 'loyalty_member', operator: 'eq', value: true }, { field: 'points_balance', operator: 'gte', value: 700 }),
  },
  {
    type: 'win_back', ruleId: 'AUT-03', name: 'Win-back', icon: 'undo',
    qualifiesWhen: 'No order for N days', reentry: 'Once per lapse; resets after the next order',
    defaultConfig: { days: 60 }, configLabel: 'Days without an order', configUnit: 'days', requiresLoyalty: false, loyaltyOverlap: null,
    defaultSms: 'We miss you, {{first_name}}! It has been a while. Come back and see what is new. {{link}}',
    defaultEmailSubject: 'We miss you at {{brand_name}}',
    defaultEmailBody: 'Hi {{first_name}}, it has been a while since your last visit. We would love to see you again.',
    defaultPushTitle: 'We miss you',
    mockAudienceRules: and({ field: 'last_order_days_ago', operator: 'between', value: [60, 90] }),
  },
  {
    type: 'high_spender_thanks', ruleId: 'AUT-09', name: 'High-spender thank you', icon: 'workspace_premium',
    qualifiesWhen: 'Enters the High spenders segment', reentry: 'Once per 180 days',
    defaultConfig: {}, configLabel: null, configUnit: null, requiresLoyalty: false, loyaltyOverlap: null,
    defaultSms: '{{first_name}}, thank you for being one of our best guests. We appreciate you. {{link}}',
    defaultEmailSubject: 'Thank you from {{brand_name}}',
    defaultEmailBody: 'Hi {{first_name}}, you are one of our most valued guests and we wanted to say thank you.',
    defaultPushTitle: 'Thank you',
    mockAudienceRules: and({ field: 'spend_top_percent', operator: 'lte', value: 10, periodDays: 180 }),
  },
  {
    type: 'post_visit', ruleId: 'AUT-02', name: 'After a visit', icon: 'restaurant',
    qualifiesWhen: 'Any order completed', reentry: 'At most once per 30 days',
    defaultConfig: { days: 1 }, configLabel: 'Days after the order', configUnit: 'days', requiresLoyalty: false, loyaltyOverlap: null,
    defaultSms: 'Thanks for visiting, {{first_name}}! Order again any time. {{link}}',
    defaultEmailSubject: 'Thanks for visiting {{brand_name}}',
    defaultEmailBody: 'Hi {{first_name}}, thank you for your order. We hope you enjoyed it.',
    defaultPushTitle: 'Thanks for visiting',
    mockAudienceRules: and({ field: 'last_order_days_ago', operator: 'lt', value: 30 }),
  },
];

/** The trigger's condition with the configured number filled in, e.g. "No order for 60 days". */
export function describeTrigger(trigger: TriggerDef, config: TriggerConfig): string {
  if (!trigger.configUnit) return trigger.qualifiesWhen;
  const n = trigger.configUnit === 'percent'
    ? `${config.percent ?? trigger.defaultConfig.percent}%`
    : String(config.days ?? trigger.defaultConfig.days);
  return trigger.qualifiesWhen.replace(/\bN\b/, n);
}

export function getTrigger(type: TriggerType | null): TriggerDef | undefined {
  return TRIGGERS.find((t) => t.type === type);
}

/** AUT-15 — when a customer qualifies for several automations on the same day, only the first in this order sends. */
export const TRIGGER_PRIORITY: TriggerType[] = [
  'birthday', 'welcome', 'loyalty_welcome', 'reward_unlocked', 'points_expiring', 'near_reward', 'win_back', 'high_spender_thanks', 'post_visit',
];

const INITIAL_STATUS: Partial<Record<TriggerType, CampaignStatus>> = { welcome: 'active', win_back: 'active', birthday: 'paused' };

/** AUT-14 — each automation ships with default copy and reward `none`; D6 — off until the merchant enables it. */
export function buildMockAutomations(): Campaign[] {
  return TRIGGERS.map((t) => {
    const status = INITIAL_STATUS[t.type] ?? 'draft';
    return {
      id: `aut_${t.type}`,
      brandId: MOCK_BRAND_ID,
      createdAt: '2026-06-01T09:00:00Z',
      updatedAt: '2026-06-01T09:00:00Z',
      name: t.name,
      kind: 'automation',
      status,
      segmentId: null,
      triggerType: t.type,
      triggerConfig: { ...t.defaultConfig },
      outletIds: [],
      channels: ['sms', 'email'],
      channelMode: 'first_available',
      content: {
        sms: { body: t.defaultSms },
        email: {
          subject: t.defaultEmailSubject, preheader: '', templateId: t.requiresLoyalty ? 'reward' : 'announcement',
          headline: t.defaultPushTitle, body: t.defaultEmailBody, imageUrl: '', ctaLabel: 'Order now', ctaTarget: 'online_ordering',
        },
      },
      rewardType: 'none',
      rewardConfig: {},
      scheduledAt: null,
      attributionWindowDays: 7,
      holdoutPercent: 0,
      createdBy: 'system',
      approvedBy: status === 'draft' ? null : 'user_owner',
      lastSentAt: null,
      audienceCount: 0,
      attributedRevenueCents: 0,
      sentLast30Days: 0,
      revenueLast30DaysCents: 0,
      spendCents: 0,
      pausedFrom: status === 'paused' ? 'active' : null,
    };
  });
}
